import type { Document } from '@gitbook/slate';
import type { MaybePromise } from '@gitbook/util-ts';

import type {
    ComputedContentSourceDocument,
    ComputedContentSourceRevision,
    DefaultComputedContentSource,
    Revision,
} from '../types';
import { ContentComputer, type RunComputeRevisionResult } from './ContentComputer';
import type {
    ComputedContentSourceDependency,
    ComputedContentSourceDependencyValue,
    ComputedContentSourceResolved,
} from './types';

export type ContentComputerFactory<
    SourceRevision extends DefaultComputedContentSource = ComputedContentSourceRevision,
    SourceDocument extends DefaultComputedContentSource = ComputedContentSourceDocument,
> = (contentId: string) => ContentComputer<SourceRevision, SourceDocument>;

export type ContentComputerFactoryContext<
    SourceRevision extends DefaultComputedContentSource = ComputedContentSourceRevision,
    SourceDocument extends DefaultComputedContentSource = ComputedContentSourceDocument,
> = {
    /**
     * Get the version of a source type.
     * It is used to determine if the source has changed.
     */
    getSourceVersion: (
        contentId: string,
        sourceType: SourceRevision['type'] | SourceDocument['type']
    ) => Promise<string>;

    /**
     * Resolve a dependency to its latest value.
     */
    resolveDependency: (
        contentId: string,
        dependency: ComputedContentSourceDependency<SourceRevision | SourceDocument>['ref'],
        onUpdate: (
            value: ComputedContentSourceDependencyValue<
                ComputedContentSourceDependency<SourceRevision | SourceDocument>
            >
        ) => void,
        onError?: (error: Error) => void
    ) => () => void;

    /**
     * Compute the set of pages for a given source.
     */
    runComputeRevision: (input: {
        contentId: string;
        computedSeed: string;
        computedSource: ComputedContentSourceResolved<SourceRevision>;
        /** Unique identifier the computation, it can be as an Idempotency key */
        computationId: string;
    }) => MaybePromise<RunComputeRevisionResult>;

    /**
     * Compute the document for a given source.
     */
    runComputeDocument: (input: {
        contentId: string;
        computedSeed: string;
        computedSource: ComputedContentSourceResolved<SourceDocument>;
        /** Unique identifier the computation, it can be as an Idempotency key */
        computationId: string;
    }) => MaybePromise<Document>;

    /**
     * Wait until a promise is resolved.
     * This is used to ensure control over the async operations when executed in an environment like Cloudflare.
     */
    waitUntil?: (promise: Promise<void>) => void;
};

export type ContentComputerFactoryStore = {
    /**
     * Get a document by its ID.
     */
    getDocument: (contentId: string, documentId: string) => MaybePromise<Document | undefined>;

    /**
     * Save a document.
     */
    setDocument: (contentId: string, documentId: string, document: Document) => MaybePromise<void>;

    /**
     * Get a revision by its ID.
     */
    getRevision: (contentId: string, revisionId: string) => MaybePromise<Revision | undefined>;

    /**
     * Save a revision.
     */
    setRevision: (contentId: string, revision: Revision) => MaybePromise<void>;
};

/**
 * Create a factory to create content computers for given contentId.
 */
export function createContentComputerFactory<
    SourceRevision extends DefaultComputedContentSource = ComputedContentSourceRevision,
    SourceDocument extends DefaultComputedContentSource = ComputedContentSourceDocument,
>({
    context,
    store,
}: {
    context: ContentComputerFactoryContext<SourceRevision, SourceDocument>;
    store: ContentComputerFactoryStore;
}): ContentComputerFactory<SourceRevision, SourceDocument> {
    const contentComputers = new Map<string, ContentComputer<SourceRevision, SourceDocument>>();

    return (contentId) => {
        const existingComputer = contentComputers.get(contentId);

        if (existingComputer) {
            return existingComputer;
        }

        const computer = new ContentComputer<SourceRevision, SourceDocument>({
            context: {
                getSourceVersion: (sourceType) => context.getSourceVersion(contentId, sourceType),
                resolveDependency: (dependency, onUpdate, onError) =>
                    context.resolveDependency(contentId, dependency, onUpdate, onError),
                runComputeRevision: (input) =>
                    context.runComputeRevision({
                        ...input,
                        contentId,
                    }),
                runComputeDocument: (input) =>
                    context.runComputeDocument({
                        ...input,
                        contentId,
                    }),
                waitUntil: context.waitUntil,
            },
            store: {
                getDocument: (documentId) => store.getDocument(contentId, documentId),
                setDocument: (documentId, document) =>
                    store.setDocument(contentId, documentId, document),
                getRevision: (revisionId) => store.getRevision(contentId, revisionId),
                setRevision: (revision) => store.setRevision(contentId, revision),
            },
        });

        contentComputers.set(contentId, computer);

        return computer;
    };
}
